import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Skeleton,
  useColorModeValue,
  Icon,
} from "@chakra-ui/react";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import client from "../utils/client";
import Layout from "../components/Layout";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const MONTH_LABELS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

// ---------- Helpers UX ----------
const last12Series = (monthlyObj) => {
  // monthlyObj: { "0": nJan, ..., "11": nDez }
  const arr = Array.from({ length: 12 }, (_, i) =>
    Number(monthlyObj?.[String(i)] || 0)
  );
  const now = new Date().getMonth(); // 0..11
  // retorna do (now-11 .. now) em ordem cronológica
  const series = [];
  const labels = [];
  for (let i = 11; i >= 0; i--) {
    const idx = (now - i + 12) % 12;
    series.push(arr[idx]);
    labels.push(MONTH_LABELS[idx]);
  }
  return { labels, series };
};

const calcMoM = (prev, curr) => {
  if (prev === 0 && curr === 0)
    return { kind: "flat", value: 0, label: "0%", color: "gray.500" };
  if (prev === 0 && curr > 0)
    return { kind: "new", value: Infinity, label: "Novo", color: "green.500" };
  const pct = ((curr - prev) / prev) * 100;
  const val = Math.round(pct * 10) / 10;
  return {
    kind: val >= 0 ? "up" : "down",
    value: val,
    label: `${val > 0 ? "+" : ""}${val.toLocaleString("pt-BR")}%`,
    color: val >= 0 ? "green.500" : "red.500",
  };
};

const makeLineOptions = (unit) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top", labels: { boxWidth: 12 } },
    tooltip: {
      callbacks: {
        label: (ctx) => ` ${ctx.parsed.y.toLocaleString("pt-BR")} ${unit}`,
      },
    },
  },
  scales: {
    x: { grid: { display: false } },
    y: {
      beginAtZero: true,
      ticks: { callback: (v) => Number(v).toLocaleString("pt-BR") },
    },
  },
  elements: { line: { tension: 0.3 }, point: { radius: 3, hoverRadius: 5 } },
});

// ---------- Component ----------
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.300");

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [videos, users, visits] = await Promise.all([
          client.get("/stats/videos"),
          client.get("/stats/users"),
          client.get("/stats/visits"),
        ]);
        // users.data esperado: { totalUsers, recentUsers, monthlyUsers: { "0":..., ... } }
        setStats({
          videos: videos.data.count,
          users: users.data,
          visits: visits.data,
        });
      } catch (err) {
        console.error("Error fetching stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Visitas
  const visits12 = useMemo(
    () => last12Series(stats?.visits?.monthlyVisits),
    [stats]
  );
  const visitPrev = visits12.series?.[visits12.series.length - 2] ?? 0;
  const visitCurr = visits12.series?.[visits12.series.length - 1] ?? 0;
  const visitMoM = useMemo(
    () => calcMoM(visitPrev, visitCurr),
    [visitPrev, visitCurr]
  );

  const visitChartData = useMemo(
    () => ({
      labels: visits12.labels || [],
      datasets: [
        {
          label: "Visitas Mensais",
          data: visits12.series || [],
          borderColor: "rgba(56, 161, 105, 1)", // green.500
          backgroundColor: "rgba(56, 161, 105, 0.18)",
          fill: true,
        },
      ],
    }),
    [visits12]
  );

  // Usuários
  const users12 = useMemo(
    () => last12Series(stats?.users?.monthlyUsers),
    [stats]
  );
  const userPrev = users12.series?.[users12.series.length - 2] ?? 0;
  const userCurr = users12.series?.[users12.series.length - 1] ?? 0;
  const userMoM = useMemo(
    () => calcMoM(userPrev, userCurr),
    [userPrev, userCurr]
  );

  const userChartData = useMemo(
    () => ({
      labels: users12.labels || [],
      datasets: [
        {
          label: "Novos Usuários / mês",
          data: users12.series || [],
          borderColor: "rgba(49, 130, 206, 1)", // blue.500
          backgroundColor: "rgba(49, 130, 206, 0.18)",
          fill: true,
        },
      ],
    }),
    [users12]
  );

  const fmt = (n) => (n ?? 0).toLocaleString("pt-BR");

  const Variation = ({ mom }) => (
    <StatHelpText color={mom.color} display="flex" alignItems="center" gap={1}>
      {mom.kind === "up" && <Icon as={TriangleUpIcon} />}
      {mom.kind === "down" && <Icon as={TriangleDownIcon} />}
      {mom.label}
      {mom.kind === "new" ? " (de 0)" : ""}
    </StatHelpText>
  );

  return (
    <Layout>
      <Flex justifyContent="center">
        <Box
          w={["100%", "95%", "92%", "90%"]}
          p={{ base: 3, md: 6 }}
          bg="white"
          mt={8}
          rounded="lg"
          boxShadow="lg"
        >
          <Heading mb={6}>Dashboard de Administração</Heading>

          {/* KPIs */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={5}>
            <Skeleton isLoaded={!loading}>
              <Box
                bg={cardBg}
                borderWidth="1px"
                borderColor={cardBorder}
                rounded="lg"
                p={5}
                shadow="sm"
              >
                <Stat>
                  <StatLabel>Total de Vídeos</StatLabel>
                  <StatNumber>{fmt(stats?.videos)}</StatNumber>
                  <StatHelpText color="gray.500">
                    Conteúdo publicado
                  </StatHelpText>
                </Stat>
              </Box>
            </Skeleton>

            <Skeleton isLoaded={!loading}>
              <Box
                bg={cardBg}
                borderWidth="1px"
                borderColor={cardBorder}
                rounded="lg"
                p={5}
                shadow="sm"
              >
                <Stat>
                  <StatLabel>Total de Usuários</StatLabel>
                  <StatNumber>{fmt(stats?.users?.totalUsers)}</StatNumber>
                  <StatHelpText color="gray.500">Base cadastrada</StatHelpText>
                </Stat>
              </Box>
            </Skeleton>

            <Skeleton isLoaded={!loading}>
              <Box
                bg={cardBg}
                borderWidth="1px"
                borderColor={cardBorder}
                rounded="lg"
                p={5}
                shadow="sm"
              >
                <Stat>
                  <StatLabel>Novos Usuários (mês)</StatLabel>
                  <StatNumber>{fmt(userCurr)}</StatNumber>
                  <Variation mom={userMoM} />
                </Stat>
              </Box>
            </Skeleton>

            <Skeleton isLoaded={!loading}>
              <Box
                bg={cardBg}
                borderWidth="1px"
                borderColor={cardBorder}
                rounded="lg"
                p={5}
                shadow="sm"
              >
                <Stat>
                  <StatLabel>Visitas (mês)</StatLabel>
                  <StatNumber>{fmt(visitCurr)}</StatNumber>
                  <Variation mom={visitMoM} />
                </Stat>
              </Box>
            </Skeleton>
          </SimpleGrid>

          {/* Gráficos */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={8}>
            <Skeleton isLoaded={!loading}>
              <Box
                bg={cardBg}
                borderWidth="1px"
                borderColor={cardBorder}
                rounded="lg"
                p={5}
                shadow="sm"
                minH="380px"
              >
                <Heading as="h2" fontSize="xl" mb={4}>
                  Visitas — últimos 12 meses
                </Heading>
                <Box h="300px">
                  <Line
                    data={visitChartData}
                    options={makeLineOptions("visitas")}
                  />
                </Box>
              </Box>
            </Skeleton>

            <Skeleton isLoaded={!loading}>
              <Box
                bg={cardBg}
                borderWidth="1px"
                borderColor={cardBorder}
                rounded="lg"
                p={5}
                shadow="sm"
                minH="380px"
              >
                <Heading as="h2" fontSize="xl" mb={4}>
                  Novos usuários — últimos 12 meses
                </Heading>
                <Box h="300px">
                  <Line
                    data={userChartData}
                    options={makeLineOptions("usuários")}
                  />
                </Box>
              </Box>
            </Skeleton>
          </SimpleGrid>

          {/* Empty states simples */}
          {!loading && visits12.series?.every((v) => v === 0) && (
            <Box mt={6} color="gray.600">
              Sem dados de visitas no período.
            </Box>
          )}
        </Box>
      </Flex>
    </Layout>
  );
};

export default Dashboard;
